import { observer } from 'mobx-react'
import { useContext, useEffect, useRef } from 'react'

import { scenarios } from '@constants/scenarios'

import { SignicatBranding } from '../SignicatBranding/SignicatBranding'
import { TopBar } from '../TopBar/TopBar'

import { StoreContext } from '@store/Root.store'
import { cx } from '@/utils/cx'
import { devLog } from '@methods/devLog'
import { getFirstPathSegment } from '@methods/urlPath'
import { PAGES, PAGES_WITHOUT_KEEP_ALIVE } from './pages'
import {
  SCENARIOS_WITH_NO_DEFAULT_BRANDING_PLACEMENTS,
  STEPS_WITH_1000_PX_BRANDING,
  STEPS_WITH_100_PERCENT_BRANDING,
  STEPS_WITH_500_PX_BRANDING,
  STEPS_WITH_940_PX_BRANDING,
  STEPS_WITH_FULL_PAGE_LAYOUT,
  STEPS_WITH_NO_DEFAULT_BRANDING_PLACEMENTS,
} from '../TopBar/constants'
import { errorMap } from '@/constants/errorMap'

export const ContentRouter: React.FC = observer(() => {
  const store = useContext(StoreContext)
  const { currentScenarioId, currentPageIndex } = store.ScenarioState
  const tempTypedScenarioId = currentScenarioId as keyof typeof scenarios

  const { isMobile } = store.AppState

  const currentStep = scenarios?.[tempTypedScenarioId]?.[currentPageIndex] || ''

  useEffect(() => {
    try {
      devLog(
        'Currently rendered page: ',
        scenarios[tempTypedScenarioId][currentPageIndex],
        `(scenario: ${tempTypedScenarioId}, page index: ${currentPageIndex})`
      )
    } catch (error) {
      devLog('Error logging current page in ContentRouter:', error)
    }
  }, [currentPageIndex, tempTypedScenarioId])

  const pathRef = useRef(getFirstPathSegment())

  useEffect(() => {
    const path = pathRef.current

    if (Object.keys(errorMap).some((v) => path === v)) {
      store.TrackingState.setShouldKeepAlive(false)
      return
    }

    if (tempTypedScenarioId === 'init') return

    const shouldKeepAlive =
      !!PAGES[currentStep as keyof typeof PAGES] && !PAGES_WITHOUT_KEEP_ALIVE.includes(currentStep)
    store.TrackingState.setShouldKeepAlive(shouldKeepAlive)
  }, [currentStep, tempTypedScenarioId])

  const renderScenarioPage = () => {
    const path = pathRef.current

    if (Object.keys(errorMap).some((v) => path === v)) {
      return <PAGES.Error />
    }

    if (tempTypedScenarioId === 'init') {
      return <PAGES.Initial />
    }

    const Handler = PAGES[currentStep as keyof typeof PAGES] || PAGES.NotFound

    return <Handler />
  }

  const getBranding = () => {
    if (
      SCENARIOS_WITH_NO_DEFAULT_BRANDING_PLACEMENTS.includes(tempTypedScenarioId) ||
      STEPS_WITH_NO_DEFAULT_BRANDING_PLACEMENTS.includes(currentStep)
    ) {
      return <></>
    }

    if (STEPS_WITH_100_PERCENT_BRANDING.includes(currentStep)) {
      return <SignicatBranding width="calc(100% - 80px)" hideOnMobile paddingTop="4px" />
    }

    if (STEPS_WITH_1000_PX_BRANDING.includes(currentStep)) {
      return <SignicatBranding width="1000px" hideOnMobile />
    }

    if (STEPS_WITH_940_PX_BRANDING.includes(currentStep)) {
      return <SignicatBranding width="940px" hideOnMobile paddingTop="4px" />
    }

    if (STEPS_WITH_500_PX_BRANDING.includes(currentStep)) {
      return <SignicatBranding width="500px" hideOnMobile />
    }

    return <SignicatBranding width="var(--container-width)" hideOnMobile />
  }

  const isFullWidth = STEPS_WITH_100_PERCENT_BRANDING.includes(currentStep)
  const isFullPage = STEPS_WITH_FULL_PAGE_LAYOUT.includes(currentStep)

  return (
    <div className={cx('content-router', !isMobile && 'layout__content-router-wrap-with-bg')}>
      <TopBar />
      <div
        className={cx(
          'layout__content-wrap',
          isFullWidth && 'layout__content-wrap-full-width',
          isFullPage && 'layout__content-wrap-full-page',
          store.AppState.iframeContent && 'layout__content-wrap-iframe'
        )}
      >
        {renderScenarioPage()}
        {getBranding()}
      </div>
    </div>
  )
})
